import React, { useEffect, useState, useContext } from 'react';
import { ConfigContext } from '../context/ConfigContext';
import { motion } from 'framer-motion';
import { 
  Users,
  CreditCard,
  Heart,
  TrendingUp,
  Calendar,
  DollarSign,
  UserCheck,
  Gift
} from 'lucide-react';

const Dashboard = () => {
  const { config } = useContext(ConfigContext);
  const [members, setMembers] = useState([]);
  const [pagos, setPagos] = useState([]);
  const [donaciones, setDonaciones] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');

  // Filtrar datos si el usuario es 'user'
  const filteredMembers = currentUser.role === 'user' ? members.filter(m => m.email === currentUser.email) : members;
  const filteredPagos = currentUser.role === 'user' ? pagos.filter(p => p.email === currentUser.email) : pagos;
  const filteredDonaciones = currentUser.role === 'user' ? donaciones.filter(d => d.email === currentUser.email) : donaciones;
  const filteredRecentActivities = currentUser.role === 'user' ? recentActivities.filter(a => a.email === currentUser.email) : recentActivities;
  const filteredUpcomingEvents = currentUser.role === 'user' ? upcomingEvents.filter(e => e.email === currentUser.email) : upcomingEvents;

  // Solo mostrar donaciones propias para el rol user
  // Buscar el miembro actual por email para obtener su id
  const miembroActual = members.find(m => m.email === currentUser.email);
  const donacionesPropias = currentUser.role === 'user'
    ? donaciones.filter(d => miembroActual && d.donorId === miembroActual.id)
    : donaciones;

  useEffect(() => {
    // Cargar datos desde la API
  fetch('https://chungwhav2.tnf.cl/api/miembros')
      .then(res => res.json())
      .then(data => setMembers(Array.isArray(data) ? data : []));

  fetch('https://chungwhav2.tnf.cl/api/pagos')
      .then(res => res.json())
      .then(data => setPagos(Array.isArray(data) ? data : []));

  fetch('https://chungwhav2.tnf.cl/api/donaciones')
      .then(res => res.json())
      .then(data => setDonaciones(Array.isArray(data) ? data : []));

  fetch('https://chungwhav2.tnf.cl/api/actividad-reciente')
      .then(res => res.json())
      .then(data => setRecentActivities(Array.isArray(data) ? data : []));

  fetch('https://chungwhav2.tnf.cl/api/eventos-proximos')
      .then(res => res.json())
      .then(data => setUpcomingEvents(Array.isArray(data) ? data : []));
  }, []);

  // Estadísticas principales solo propias
  if (currentUser.role === 'user') {
    const totalMiembros = filteredMembers.length;
    const miembrosActivos = filteredMembers.filter(m => m.status === 'active').length;
    const miembrosVIP = filteredMembers.filter(m => m.membershipType === 'vip').length;
    const totalPagosMes = filteredPagos
      .filter(p => new Date(p.paymentDate).getMonth() === new Date().getMonth())
      .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
    const totalDonaciones = filteredDonaciones.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);

    const stats = [
      {
        title: 'Total de Socios',
        value: totalMiembros.toLocaleString(),
        change: '',
        icon: Users,
        color: 'from-blue-500 to-cyan-500',
        bgColor: 'from-blue-50 to-cyan-50'
      },
      {
        title: 'Ingresos del Mes',
        value: `$${totalPagosMes.toLocaleString()}`,
        change: '',
        icon: DollarSign,
        color: 'from-green-500 to-emerald-500',
        bgColor: 'from-green-50 to-emerald-50'
      },
      {
        title: 'Total de Donaciones',
        value: `$${totalDonaciones.toLocaleString()}`,
        change: '',
        icon: Heart,
        color: 'from-red-500 to-pink-500',
        bgColor: 'from-red-50 to-pink-50'
      },
      {
        title: 'Socios Activos',
        value: miembrosActivos.toLocaleString(),
        change: '',
        icon: UserCheck,
        color: 'from-purple-500 to-indigo-500',
        bgColor: 'from-purple-50 to-indigo-50'
      }
    ];

    // Mostrar solo la ficha del usuario logeado

    // Utilidad para fecha corta
    function fechaCorta(fecha) {
      if (!fecha) return '-';
      const d = new Date(fecha);
      return d.toLocaleDateString('es-CL', { year: 'numeric', month: '2-digit', day: '2-digit' });
    }

    // Estado en español
    function estadoEsp(estado) {
      if (estado === 'active') return 'Activo';
      if (estado === 'inactive') return 'Inactivo';
      return estado;
    }

    // Pagos del mes actual para el usuario usando el id del miembro
    const now = new Date();
    const currentMonth = String(now.getMonth() + 1).padStart(2, '0');
    const currentYear = now.getFullYear();
    let totalPagosMesUser = 0;
    if (miembroActual) {
      totalPagosMesUser = pagos
        .filter(p => p.memberId === miembroActual.id)
        .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
    }

    const ultimosPagos = pagos
      .filter(p => p.memberId === (miembroActual && miembroActual.id))
      .sort((a, b) => new Date(b.paymentDate) - new Date(a.paymentDate))
      .slice(0, 3);
    const ultimasDonaciones = donacionesPropias.slice(-3).reverse();

    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="bg-gradient-to-br from-blue-50 to-purple-50 border border-gray-200/50 rounded-2xl p-8 shadow-xl w-full max-w-lg"
        >
          <div className="flex flex-col items-center gap-4">
            <div className="w-20 h-20 sm:w-28 sm:h-28 rounded-full overflow-hidden border-4 border-blue-400 shadow-lg">
              {miembroActual && miembroActual.photoUrl ? (
                <img src={miembroActual.photoUrl} alt="Foto de perfil" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-blue-200 text-blue-700 font-bold text-5xl">
                  {miembroActual && miembroActual.name ? miembroActual.name.charAt(0) : '?'}
                </div>
              )}
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-1">{miembroActual ? miembroActual.name : currentUser.name}</h2>
            <p className="text-lg text-gray-600 font-medium mb-2">{miembroActual ? miembroActual.email : currentUser.email}</p>
            <div className="flex flex-wrap gap-3 justify-center mb-2">
              <span className="px-4 py-2 bg-blue-100 text-blue-700 rounded-xl font-semibold">RUT: {miembroActual && miembroActual.rut ? miembroActual.rut : '-'}</span>
              <span className="px-4 py-2 bg-purple-100 text-purple-700 rounded-xl font-semibold">Membresía: {miembroActual ? miembroActual.membershipType : '-'}</span>
              <span className="px-4 py-2 bg-green-100 text-green-700 rounded-xl font-semibold">Estado: {miembroActual ? estadoEsp(miembroActual.status) : '-'}</span>
              <span className="px-4 py-2 bg-orange-100 text-orange-700 rounded-xl font-semibold">
                Cantidad de Familia: {
                  miembroActual
                    ? `${parseInt(miembroActual.familia_adultos || 0)} adulto(s)${miembroActual.familia_ninos ? `, ${parseInt(miembroActual.familia_ninos)} niño(s)` : ''}`
                    : '-'
                }
              </span>
            </div>
            <div className="flex flex-wrap gap-3 justify-center mb-2">
              <span className="px-4 py-2 bg-yellow-100 text-yellow-700 rounded-xl font-semibold">Fecha de ingreso: {miembroActual && miembroActual.joinDate ? fechaCorta(miembroActual.joinDate) : '-'}</span>
              <span className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl font-semibold">Teléfono: {miembroActual && miembroActual.phone ? miembroActual.phone : '-'}</span>
              <span className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl font-semibold">Dirección: {miembroActual && miembroActual.address ? miembroActual.address : '-'}</span>
            </div>
            <div className="w-full grid grid-cols-2 gap-4 mb-4">
              <div className="bg-gradient-to-br from-blue-100 to-cyan-100 rounded-xl p-4 text-center">
                <span className="block text-sm text-gray-500">Total Pagado</span>
                <span className="text-xl font-bold text-blue-700">${totalPagosMesUser.toLocaleString('es-CL')}</span>
              </div>
              <div className="bg-gradient-to-br from-pink-100 to-red-100 rounded-xl p-4 text-center">
                <span className="block text-sm text-gray-500">Total donado</span>
                <span className="text-xl font-bold text-pink-700">${donacionesPropias.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0).toLocaleString('es-CL')}</span>
              </div>
            </div>
            <div className="w-full grid grid-cols-2 gap-4 mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Últimos Pagos</h3>
                {ultimosPagos.length === 0 ? (
                  <div className="text-gray-400">No tienes pagos registrados.</div>
                ) : (
                  <ul className="space-y-2">
                    {ultimosPagos.map((pago, idx) => (
                      <li key={pago.id || idx} className="bg-white border rounded-xl p-3 shadow-sm flex flex-col">
                        <span className="font-semibold text-gray-900">{pago.concepto || 'Pago'}</span>
                        <span className="text-gray-600">${Number(pago.amount).toLocaleString('es-CL')}</span>
                        <span className="text-gray-500 text-sm">{pago.paymentDate ? fechaCorta(pago.paymentDate) : ''}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Últimas Donaciones</h3>
                {ultimasDonaciones.length === 0 ? (
                  <div className="text-gray-400">No tienes donaciones registradas.</div>
                ) : (
                  <ul className="space-y-2">
                    {ultimasDonaciones.map((don, idx) => (
                      <li key={don.id || idx} className="bg-white border rounded-xl p-3 shadow-sm flex flex-col">
                        <span className="font-semibold text-gray-900">{don.concepto || 'Donación'}</span>
                        <span className="text-gray-600">${Number(don.amount).toLocaleString('es-CL')}</span>
                        <span className="text-gray-500 text-sm">{don.donationDate ? fechaCorta(don.donationDate) : (don.date ? fechaCorta(don.date) : '')}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  // ...dashboard para admin...
  // Dashboard completo para admin
  const totalMiembros = members.length;
  const miembrosActivos = members.filter(m => m.status === 'active').length;
  const miembrosVIP = members.filter(m => m.membershipType === 'vip').length;
  const totalPagosMes = pagos
    .filter(p => new Date(p.paymentDate).getMonth() === new Date().getMonth())
    .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  const totalDonaciones = donaciones.reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);

  const stats = [
    {
      title: 'Total de Socios',
      value: totalMiembros.toLocaleString(),
      change: '',
      icon: Users,
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'from-blue-50 to-cyan-50'
    },
    {
      title: 'Ingresos del Mes',
      value: `$${totalPagosMes.toLocaleString()}`,
      change: '',
      icon: DollarSign,
      color: 'from-green-500 to-emerald-500',
      bgColor: 'from-green-50 to-emerald-50'
    },
    {
      title: 'Total de Donaciones',
      value: `$${totalDonaciones.toLocaleString()}`,
      change: '',
      icon: Heart,
      color: 'from-red-500 to-pink-500',
      bgColor: 'from-red-50 to-pink-50'
    },
    {
      title: 'Socios Activos',
      value: miembrosActivos.toLocaleString(),
      change: '',
      icon: UserCheck,
      color: 'from-purple-500 to-indigo-500',
      bgColor: 'from-purple-50 to-indigo-50'
    }
  ];

  return (
  <div className="space-y-6 px-2 sm:px-4 md:px-8">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {config.loginLogo ? (
          <img src={config.loginLogo} alt="Logo" className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl mb-2 object-contain" />
        ) : null}
        <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-600 bg-clip-text text-transparent mb-2">
          Dashboard
        </h1>
        <p className="text-gray-600">Bienvenido al panel de administración de {config.organizationName || 'BeneficiaHub'}</p>
      </motion.div>
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={stat.title}
              className={`bg-gradient-to-br ${stat.bgColor} border border-gray-200/50 rounded-2xl p-6 shadow-sm hover:shadow-lg transition-all duration-300`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, duration: 0.5 }}
              whileHover={{ scale: 1.02 }}
            >
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 bg-gradient-to-br ${stat.color} rounded-xl`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
                {stat.change && (
                  <span className="text-green-600 text-sm font-semibold bg-green-100 px-2 py-1 rounded-lg">
                    {stat.change}
                  </span>
                )}
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-1">{stat.value}</h3>
              <p className="text-gray-600 font-medium">{stat.title}</p>
            </motion.div>
          );
        })}
      </div>
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-6">
        {/* Actividades recientes - formato original */}
        <motion.div
          className="bg-white/90 backdrop-blur-xl border border-gray-200/50 rounded-2xl p-4 sm:p-6 shadow-xl"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5, duration: 0.5 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <TrendingUp className="w-6 h-6 text-blue-500" />
            <h2 className="text-xl font-bold text-gray-900">Actividad Reciente</h2>
          </div>
          <div className="space-y-4">
            {recentActivities.length === 0 && (
              <div className="text-gray-400 text-center">Sin actividad reciente</div>
            )}
            {recentActivities.map((activity, index) => (
              <motion.div
                key={activity.id || index}
                className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors duration-200"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 + index * 0.1 }}
              >
                <div className={`p-2 rounded-lg ${
                  activity.type === 'payment' ? 'bg-green-100 text-green-600' :
                  activity.type === 'donation' ? 'bg-red-100 text-red-600' :
                  activity.type === 'member' ? 'bg-blue-100 text-blue-600' :
                  'bg-purple-100 text-purple-600'
                }`}>
                  {activity.type === 'payment' && <CreditCard className="w-4 h-4" />}
                  {activity.type === 'donation' && <Heart className="w-4 h-4" />}
                  {activity.type === 'member' && <Users className="w-4 h-4" />}
                  {activity.type === 'birthday' && <Gift className="w-4 h-4" />}
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{activity.user}</p>
                  <p className="text-sm text-gray-500">
                    {(() => {
                      if (activity.amount === undefined || activity.amount === null) return '';
                      if (typeof activity.amount === 'number') {
                        return `$${activity.amount.toLocaleString('es-CL')}`;
                      }
                      if (typeof activity.amount === 'string' && activity.amount.match(/^[\d]{1,3}(\.\d{3})*$/)) {
                        return `$${activity.amount}`;
                      }
                      // Si es string y no tiene puntos, convertir y formatear
                      const clean = activity.amount.replace(/\$/g, '');
                      const num = Number(clean);
                      return isNaN(num) ? activity.amount : `$${num.toLocaleString('es-CL')}`;
                    })()}
                  </p>
                </div>
                <span className="text-xs text-gray-400">{activity.time}</span>
              </motion.div>
            ))}
          </div>
        </motion.div>
        {/* Próximos eventos - formato original */}
        <motion.div
          className="bg-white/90 backdrop-blur-xl border border-gray-200/50 rounded-2xl p-4 sm:p-6 shadow-xl"
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.5, duration: 0.5 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <Calendar className="w-6 h-6 text-purple-500" />
            <h2 className="text-xl font-bold text-gray-900">Próximos Eventos</h2>
          </div>
          <div className="space-y-4">
            {upcomingEvents.length === 0 && (
              <div className="text-gray-400 text-center">Sin eventos próximos</div>
            )}
            {upcomingEvents.map((event, index) => (
              <motion.div
                key={event.id || index}
                className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors duration-200"
                initial={{ opacity: 0, x: 10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 + index * 0.1 }}
              >
                <div className={`p-2 rounded-lg ${
                  event.type === 'birthday' ? 'bg-pink-100 text-pink-600' :
                  event.type === 'payment' ? 'bg-orange-100 text-orange-600' :
                  'bg-indigo-100 text-indigo-600'
                }`}>
                  {event.type === 'birthday' && <Gift className="w-4 h-4" />}
                  {event.type === 'payment' && <CreditCard className="w-4 h-4" />}
                  {event.type === 'event' && <Calendar className="w-4 h-4" />}
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{event.title}</p>
                  <p className="text-sm text-gray-500">{event.date}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Dashboard;
